/**
 * A list of readable notifications as used by the Bibliotron global nav.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";

type Props = {
    // Whether or not new notifications are loading.
    loading: boolean;
    // A list of notifications to render.
    notifications: Array<any>;
    // An event triggered when the dropdown is closed.
    onDropdownClose: () => unknown;
    // A callback function that allows the LearnMenu to grab a reference to
    // the first navigable child. This is used when a user first navigates
    // down into the menu.
    captureFirstNavigableChildRef: (
        node?: HTMLElement | null | undefined,
    ) => unknown;
    // Used to navigate up the dropdown menu beyond this list.
    navigateToNotificationsSwitch: (arg1: React.KeyboardEvent) => unknown;
    // If we want users to be able to traverse this list using tab keys
    // (as opposed to arrow keys).
    tabbable?: boolean;
};

type State = {
    ReadableNotification: any;
};

export default class NotificationsList extends React.Component<Props, State> {
    static defaultProps: {
        captureFirstNavigableChildRef: () => void;
        loading: boolean;
        navigateToNotificationsSwitch: () => void;
        notifications: Array<any>;
        onDropdownClose: () => void;
    } = {
        loading: false,
        notifications: [],
        onDropdownClose: () => {},
        captureFirstNavigableChildRef: () => {},
        navigateToNotificationsSwitch: () => {},
    };

    state: State = {
        ReadableNotification: null,
    };

    componentDidMount() {
        import("../notifications-package/readable-notification.tsx")
            .then((module) => module.default)
            .then((ReadableNotification) => {
                if (!this._hasUnmounted) {
                    this.setState({ReadableNotification});
                }
            });
    }

    componentWillUnmount() {
        this._hasUnmounted = true;
    }

    // The list of links in this dropdown that the user can navigate to using arrow keys.
    navigableChildren: Array<HTMLElement> = [];

    _hasUnmounted = false;

    setLinkRef(node: HTMLElement, navigableListIndex: number) {
        const {captureFirstNavigableChildRef} = this.props;

        this.navigableChildren[navigableListIndex] = node;

        if (navigableListIndex === 0 && captureFirstNavigableChildRef) {
            captureFirstNavigableChildRef(node);
        }
    }

    getClickedNavigableIndex(event: React.KeyboardEvent<HTMLElement>): number {
        if (
            event.target instanceof HTMLElement &&
            event.target.parentNode instanceof HTMLElement
        ) {
            return parseInt(
                // @ts-expect-error - TS2345 - Argument of type 'string | null' is not assignable to parameter of type 'string'.
                event.target.parentNode.getAttribute("data-navigable-index"),
            );
        }

        return -1;
    }

    tryToFocusOnElementByIndex(index: number) {
        const nextElement: HTMLElement | null | undefined =
            this.navigableChildren[index];

        if (
            nextElement &&
            nextElement.firstChild &&
            nextElement.firstChild instanceof HTMLElement
        ) {
            nextElement.firstChild.focus();
        }
    }

    handleonKeyUpLink(event: React.KeyboardEvent<HTMLElement>) {
        const {navigateToNotificationsSwitch, onDropdownClose} = this.props;

        if (event.key === "Escape") {
            event.preventDefault();
            if (onDropdownClose) {
                onDropdownClose();
            }
        } else if (event.key === "Tab") {
            // We explicitly don't call event.preventDefault on tabbing so the user can
            // tab to the next node in the DOM tabbing order. We just close the menu
            // since they're leaving
            if (onDropdownClose) {
                onDropdownClose();
            }
        } else if (event.key === "ArrowDown") {
            event.preventDefault();

            const currentIndex = this.getClickedNavigableIndex(event);

            // If we're at the end of the list of links, we do nothing because there's
            // nowhere left to go.
            if (currentIndex === this.navigableChildren.length - 1) {
                return;
            }

            this.tryToFocusOnElementByIndex(currentIndex + 1);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();

            const currentIndex: number = this.getClickedNavigableIndex(event);

            if (currentIndex === 0) {
                // We're at the first link, but because there's a separate button above
                // this list in the dropdown, we need to navigate to that link.
                if (navigateToNotificationsSwitch) {
                    navigateToNotificationsSwitch(event);
                }
            } else {
                this.tryToFocusOnElementByIndex(currentIndex - 1);
            }
        }
    }

    render(): React.ReactNode {
        const {loading, notifications, tabbable} = this.props;
        const {ReadableNotification} = this.state;

        const hasNotifications = notifications.length > 0;
        const showSpinner =
            loading || (hasNotifications && !ReadableNotification);

        return (
            <ul data-testid="notifications-dropdown">
                {!loading && !hasNotifications && (
                    <li>
                        <Body style={styles.empty}>
                            {t`No notifications. You can get back to learning!`}
                        </Body>
                    </li>
                )}
                {ReadableNotification &&
                    notifications.map((notification, i) => (
                        <li key={i}>
                            <ReadableNotification
                                {...notification}
                                innerRef={(node) => this.setLinkRef(node, i)}
                                onKeyUp={(event) =>
                                    this.handleonKeyUpLink(event)
                                }
                                indexInNotificationsList={i}
                                tabbable={tabbable}
                            />
                        </li>
                    ))}
                {showSpinner && (
                    <li
                        className={css(
                            styles.empty,
                            hasNotifications && styles.loadingMore,
                        )}
                    >
                        <CircularSpinner size="medium" style={styles.spinner} />
                    </li>
                )}
            </ul>
        );
    }
}

const styles = StyleSheet.create({
    empty: {
        display: "flex",
        height: 150,
        placeItems: "center",
        padding: spacing.medium_16,
    },

    loadingMore: {
        height: 18,
    },

    spinner: {
        alignItems: "center",
    },
});
